import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, UtensilsCrossed, Star, Heart, Clock, Grid, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import RecipeCard from '../components/RecipeCard';
import RecipeListItem from '../components/RecipeListItem';
import NavigationMenu from '../components/NavigationMenu';
import UsageLimitBanner from '../components/UsageLimitBanner';
import CancelledSubscriptionBanner from '../components/CancelledSubscriptionBanner';
import WelcomeBanner from '../components/WelcomeBanner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [allRecipes, setAllRecipes] = useState([]); // Todas as receitas carregadas
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showSearch, setShowSearch] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // NOVO: Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [recipesPerPage, setRecipesPerPage] = useState(20); // Padrão: 20 por página
  const [totalRecipes, setTotalRecipes] = useState(0);

  useEffect(() => {
    fetchCategories();
    fetchRecipes();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchRecipes = async (category = null, search = null, filter = null) => {
    try {
      setLoading(true);
      let url = `${API}/recipes?limit=1000`; // Buscar todas para paginar no frontend
      if (category && category !== 'all') {
        url += `&category=${encodeURIComponent(category)}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (filter && filter !== 'all') {
        url += `&filter_type=${filter}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      
      setAllRecipes(response.data);
      setTotalRecipes(response.data.length);
      setCurrentPage(1); // Reset para primeira página
      
      // Aplicar paginação
      paginateRecipes(response.data, 1, recipesPerPage);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      toast.error(t('recipe.errorLoadingRecipes'));
    } finally {
      setLoading(false);
    }
  };
  
  // NOVO: Função para paginar receitas
  const paginateRecipes = (allData, page, perPage) => {
    if (perPage === 'all') {
      setRecipes(allData);
      setTotalRecipes(allData.length);
    } else {
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginated = allData.slice(startIndex, endIndex);
      setRecipes(paginated);
      setTotalRecipes(allData.length);
    }
  };
  
  // NOVO: Mudar página
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    paginateRecipes(allRecipes, newPage, recipesPerPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // NOVO: Mudar receitas por página
  const handleRecipesPerPageChange = (value) => {
    setRecipesPerPage(value);
    setCurrentPage(1);
    paginateRecipes(allRecipes, 1, value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecipes(selectedCategory === 'all' ? null : selectedCategory, searchQuery, filterType);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    fetchRecipes(value === 'all' ? null : value, searchQuery || null, filterType);
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    fetchRecipes(selectedCategory === 'all' ? null : selectedCategory, searchQuery || null, type);
  };

  return (
    <div className="min-h-screen">
      <NavigationMenu />
      
      
      {/* Recipe Type Filter - ALWAYS VISIBLE */}
      <div className="bg-white py-4 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center gap-2 flex-wrap items-center">
            {/* Toggle Search Button */}
            <Button
              onClick={() => setShowSearch(!showSearch)}
              variant="outline"
              size="sm"
              className="border-blue-300 text-blue-700 hover:bg-blue-50 rounded-full"
            >
              <Search className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">
                {showSearch ? t('common.hideSearch') : t('common.search')}
              </span>
            </Button>
            
            <div className="w-px h-6 bg-gray-300 mx-2 hidden sm:block"></div>
            
            <Button
              data-testid="filter-mine"
              onClick={() => handleFilterChange('mine')}
              variant={filterType === 'mine' ? 'default' : 'outline'}
              className={`${
                filterType === 'mine'
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'border-amber-300 text-amber-900 hover:bg-orange-50'
              } rounded-full px-3 sm:px-6 text-xs sm:text-sm transition-all`}
            >
              {t('recipe.myRecipes')}
            </Button>
            <Button
              data-testid="filter-family"
              onClick={() => handleFilterChange('family')}
              variant={filterType === 'family' ? 'default' : 'outline'}
              className={`${
                filterType === 'family'
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'border-amber-300 text-amber-900 hover:bg-orange-50'
              } rounded-full px-3 sm:px-6 text-xs sm:text-sm transition-all`}
            >
              {t('recipe.familyRecipes')}
            </Button>
            <Button
              data-testid="filter-all"
              onClick={() => handleFilterChange('all')}
              variant={filterType === 'all' ? 'default' : 'outline'}
              className={`${
                filterType === 'all'
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'border-amber-300 text-amber-900 hover:bg-orange-50'
              } rounded-full px-3 sm:px-6 text-xs sm:text-sm transition-all`}
            >
              {t('recipe.allRecipes')}
            </Button>
            
            <div className="hidden sm:block w-px h-6 bg-gray-300 mx-2"></div>
            
            {/* View Mode Toggle */}
            <div className="flex gap-1 border border-gray-300 rounded-full p-1">
              <Button
                onClick={() => setViewMode('grid')}
                variant="ghost"
                size="sm"
                className={`rounded-full ${viewMode === 'grid' ? 'bg-orange-100 text-orange-700' : 'text-gray-600'}`}
                title={t('recipe.viewGrid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setViewMode('list')}
                variant="ghost"
                size="sm"
                className={`rounded-full ${viewMode === 'list' ? 'bg-orange-100 text-orange-700' : 'text-gray-600'}`}
                title={t('recipe.viewList')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section - COLLAPSIBLE */}
      <div className={`bg-gradient-to-b from-white to-amber-50/30 py-6 border-b border-amber-100 ${showSearch ? '' : 'hidden'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 w-full">
              <div className="flex gap-2">
                <div className="relative group flex-1">
                  <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-amber-400 w-5 h-5 transition-colors group-hover:text-orange-500" />
                  <Input
                    data-testid="search-input"
                    type="text"
                    placeholder={t('recipe.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-14 pr-6 py-4 rounded-2xl border-2 border-amber-200/50 bg-white/80 backdrop-blur-sm focus:border-orange-400 focus:bg-white transition-all duration-300 shadow-sm hover:shadow-md text-gray-700 placeholder:text-gray-400"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </form>

            {/* Category Filter */}
            <div className="w-full sm:w-72">
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger 
                  data-testid="category-filter" 
                  className="py-4 px-6 rounded-2xl border-2 border-amber-200/50 bg-white/80 backdrop-blur-sm hover:border-orange-400 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <SelectValue placeholder={t('recipe.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('recipe.allCategories')}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Recipes Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Usage Limit Banner */}
        <UsageLimitBanner className="mb-8" />
        
        {/* Welcome Banner - For new users in trial */}
        <WelcomeBanner key={`welcome-${user?.id}`} className="mb-6" />
        
        {/* Cancelled Subscription Banner */}
        <CancelledSubscriptionBanner key={`cancelled-${user?.id}`} className="mb-4" />
        
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">{t('recipe.loadingRecipes')}</p>
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-20">
            <UtensilsCrossed className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">{t('recipe.noRecipesFound')}</h3>
            <p className="text-gray-500 mb-6">{t('recipe.startCreating')}</p>
            <Button 
              onClick={() => navigate('/create')} 
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('recipe.createRecipe')}
            </Button>
          </div>
        ) : (
          <>
            {/* Controles de Paginação - Topo */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 bg-white/50 backdrop-blur-sm p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t('common.show')}:</span>
                <Select value={recipesPerPage.toString()} onValueChange={(v) => handleRecipesPerPageChange(v === 'all' ? 'all' : parseInt(v))}>
                  <SelectTrigger className="w-32 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 {t('common.recipes')}</SelectItem>
                    <SelectItem value="30">30 {t('common.recipes')}</SelectItem>
                    <SelectItem value="50">50 {t('common.recipes')}</SelectItem>
                    <SelectItem value="all">{t('recipe.allRecipes')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="text-sm text-gray-600">
                {recipesPerPage === 'all' 
                  ? `${t('common.total')}: ${totalRecipes} ${t('common.recipes')}`
                  : `${t('common.showing')} ${recipes.length} ${t('common.of')} ${totalRecipes} ${t('common.recipes')}`
                }
              </div>
            </div>

            {/* Grid de Receitas */}
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'
              : 'flex flex-col gap-4'
            }>
              {recipes.map((recipe) => (
                viewMode === 'grid' ? (
                  <RecipeCard key={recipe.id} recipe={recipe} onRecipeUpdate={fetchRecipes} />
                ) : (
                  <RecipeListItem key={recipe.id} recipe={recipe} onRecipeUpdate={fetchRecipes} />
                )
              ))}
            </div>
            
            {/* Paginação - Rodapé */}
            {recipesPerPage !== 'all' && totalRecipes > recipesPerPage && (
              <div className="mt-12 flex justify-center items-center gap-2">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className="border-orange-300 hover:bg-orange-50"
                >
                  {t('common.previous')}
                </Button>
                
                <div className="flex gap-1">
                  {Array.from({ length: Math.ceil(totalRecipes / recipesPerPage) }, (_, i) => i + 1)
                    .filter(page => {
                      // {t('common.show')}: primeira, última, atual, e ±2 da atual
                      const totalPages = Math.ceil(totalRecipes / recipesPerPage);
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 2 && page <= currentPage + 2);
                    })
                    .map((page, index, array) => {
                      // Adicionar "..." entre números não consecutivos
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                          <Button
                            onClick={() => handlePageChange(page)}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            className={`min-w-[2.5rem] ${
                              currentPage === page
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'border-gray-300 hover:bg-orange-50'
                            }`}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      );
                    })
                  }
                </div>
                
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === Math.ceil(totalRecipes / recipesPerPage)}
                  variant="outline"
                  size="sm"
                  className="border-orange-300 hover:bg-orange-50"
                >
                  {t('common.next')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HomePage;